<template>
  <v-dialog v-model="model" max-width="1200px" persistent>
    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>{{ $t('Importer des véhicules (Excel)') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close" :disabled="busy">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text>
        <v-alert color="secondary" outlined class="mb-4 mt-3">
          {{
            $t(
              'Sélectionnez un fichier Excel conforme au modèle. Vous pouvez éditer les lignes avant import. Les lignes invalides sont surlignées.'
            )
          }}
          <div class="mt-2">
            <v-btn text small @click="downloadTemplate" :disabled="busy">
              <v-icon left>mdi-download</v-icon>{{ $t('Télécharger le modèle') }}
            </v-btn>
          </div>
        </v-alert>

        <v-row class="mt-3 mb-3">
          <v-col cols="12" md="6">
            <v-file-input
              v-model="file"
              :label="$t('Fichier Excel (.xlsx)')"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              prepend-icon="mdi-microsoft-excel"
              outlined
              dense
              show-size
              :disabled="busy"
              @change="onFileChange"
            />
          </v-col>

          <!-- Valeurs par défaut (facultatives) -->
          <v-col cols="12" md="3">
            <v-select
              v-model="defaultDepartmentId"
              :items="departments"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              :disabled="busy"
              :label="$t('Département par défaut')"
              @change="validateAll"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="defaultProductId"
              :items="products"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
              :disabled="busy"
              :label="$t('Carburant par défaut')"
              @change="validateAll"
            />
          </v-col>
        </v-row>

        <v-alert v-if="errors.length" color="error" outlined class="mb-4">
          <div class="font-weight-bold mb-1">{{ $t('Erreurs de parsing') }}</div>
          <div v-for="(e, i) in errors" :key="i">- {{ e }}</div>
        </v-alert>

        <div v-if="rows.length">
          <div class="d-flex justify-space-between align-center mb-2">
            <div class="subtitle-2 grey--text">
              {{ $t('Aperçu & édition') }} — {{ rows.length }} {{ $t('ligne(s) détectée(s)') }}
            </div>
            <div class="d-flex align-center">
              <v-chip class="mr-2" small>
                {{ validCount }} {{ $t('valide(s)') }}
              </v-chip>
              <v-chip
                v-if="invalidCount"
                color="error"
                text-color="white"
                small
              >
                {{ invalidCount }} {{ $t('en erreur') }}
              </v-chip>
            </div>
          </div>

          <v-data-table
            :headers="tableHeaders"
            :items="rows"
            :item-class="getRowClass"
            :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            dense
            class="elevation-1 bgTable"
          >
            <!-- Erreurs -->
            <template v-slot:item._errors="{ item }">
              <div v-if="item._errors && item._errors.length">
                <v-chip color="error" text-color="white" small class="mr-1">
                  {{ item._errors[0] }}<span v-if="item._errors.length > 1">
                    (+{{ item._errors.length - 1 }})
                  </span>
                </v-chip>
              </div>
            </template>

            <!-- Cellules éditables texte -->
            <template
              v-for="col in editableText"
              v-slot:[`item.${col}`]="{ item }"
            >
              <v-edit-dialog
                :key="col + '_' + item._row"
                :return-value.sync="item[col]"
                @save="validateRow(item)"
              >
                <span>{{ item[col] }}</span>
                <template v-slot:input>
                  <v-text-field
                    v-model="item[col]"
                    dense
                    outlined
                    hide-details
                  />
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.year="{ item }">
              <v-edit-dialog
                :return-value.sync="item.year"
                @save="
                  () => {
                    item.year = parseIntOrEmpty(item.year)
                    validateRow(item)
                  }
                "
              >
                <span>{{ item.year }}</span>
                <template v-slot:input>
                  <v-text-field
                    v-model="item.year"
                    type="number"
                    dense
                    outlined
                    hide-details
                  />
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.is_kmh="{ item }">
              <v-switch v-model="item.is_kmh" inset @change="validateRow(item)" />
            </template>

            <template v-slot:item.is_blocked="{ item }">
              <v-switch
                v-model="item.is_blocked"
                inset
                @change="validateRow(item)"
              />
            </template>

            <template v-slot:item.department="{ item }">
              <v-edit-dialog
                :return-value.sync="item.department"
                @save="
                  () => {
                    item.department = resolveDepartmentToIdOrKeep(
                      item.department
                    )
                    validateRow(item)
                  }
                "
              >
                <span>
                  {{
                    resolveDepartmentName(item.department) ||
                    item.department ||
                    '-'
                  }}
                </span>
                <template v-slot:input>
                  <v-select
                    :items="departments"
                    item-text="name"
                    item-value="id"
                    v-model="item.department"
                    dense
                    outlined
                    hide-details
                    clearable
                  />
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.product="{ item }">
              <v-edit-dialog
                :return-value.sync="item.product"
                @save="
                  () => {
                    item.product = resolveProductToIdOrKeep(item.product)
                    validateRow(item)
                  }
                "
              >
                <span>
                  {{
                    resolveProductName(item.product) || item.product || '-'
                  }}
                </span>
                <template v-slot:input>
                  <v-select
                    :items="products"
                    item-text="name"
                    item-value="id"
                    v-model="item.product"
                    dense
                    outlined
                    hide-details
                    clearable
                  />
                </template>
              </v-edit-dialog>
            </template>
          </v-data-table>

          <div class="text-caption mt-1 grey--text">
            {{
              $t(
                "Double-cliquez sur une cellule pour l'éditer. Les valeurs invalides peuvent être corrigées ici."
              )
            }}
          </div>
        </div>

        <div v-if="busy" class="mt-4">
          <v-progress-linear :value="progress" height="8" />
          <div class="text-caption mt-1">
            {{ $t('Import en cours') }}… {{ done }}/{{ total }}
          </div>
        </div>

        <v-alert
          v-if="result.errors && result.errors.length"
          color="warning"
          outlined
          class="mt-4"
        >
          <div class="font-weight-bold mb-1">
            {{ $t('Erreurs côté API') }}
          </div>
          <div v-for="(e, i) in result.errors" :key="'e' + i">- {{ e }}</div>
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="busy">
          {{ $t('commons.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!canImport || invalidCount === rows.length || busy"
          @click="importRows"
        >
          <v-icon left>mdi-upload</v-icon>{{ $t('Importer') }} ({{
            validCount
          }}/{{ rows.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import CompanyVehiclesService from '@/services/CompanyVehiclesService'
import { ProductDetailService, DepartmentService } from '@/services'

/** En-têtes attendues (FR) — carte grise retirée */
const EXPECTED_HEADERS = {
  registration_number: ['N° Matricule', 'Matricule', 'registration_number'],
  make: ['Marque', 'make'],
  model: ['Modèle', 'Model', 'model'],
  year: ['Année', 'year'],
  color: ['Couleur', 'color'],
  vin: ['N° Châssis', 'N° Chassis', 'Chassis', 'VIN', 'vin'],
  department: ['Département', 'Department', 'department', 'department_id'],
  product: [
    'Type de carburant',
    'Type de Carburant',
    'Produit',
    'product',
    'product_id'
  ],
  vehicle_type: ['Type de véhicule', 'Type de vehicule', 'vehicle_type'],
  driver_last_name: ['Nom du conducteur', 'Nom', 'driver_last_name'],
  driver_first_name: [
    'Prénom du conducteur',
    'Prenom du conducteur',
    'Prénom',
    'driver_first_name'
  ],
  driver_phone_number: [
    'Téléphone conducteur',
    'N° de conducteur',
    'driver_phone_number'
  ],
  secret_code: ['Code secret', 'secret_code'],
  is_kmh: ['Suivi kmh (Oui/Non)', 'is_kmh'],
  is_blocked: ['Bloqué (Oui/Non)', 'is_blocked']
}

// Codes autorisés (Django)
const VEHICLE_TYPE_ALLOWED = [
  'Motorcycle',
  'Truck',
  'Tricycle',
  'Sedan',
  'SUV',
  'PICK-UP',
  'OTHER',
  'VL'
]

export default {
  name: 'ImportVehicleDialog',
  props: { value: { type: Boolean, default: false } },
  data () {
    return {
      model: this.value,
      file: null,
      rows: [],
      mapping: {},
      errors: [],
      products: [],
      departments: [],
      defaultDepartmentId: null,
      defaultProductId: null,

      busy: false,
      total: 0,
      done: 0,
      progress: 0,
      result: { errors: [] }
    }
  },
  watch: {
    value (v) {
      this.model = v
    },
    model (v) {
      this.$emit('input', v)
    }
  },
  computed: {
    tableHeaders () {
      return [
        { text: this.$t('Erreurs'), value: '_errors', width: 220 },
        { text: this.$t('N° Matricule'), value: 'registration_number' },
        { text: this.$t('Marque'), value: 'make' },
        { text: this.$t('Modèle'), value: 'model' },
        { text: this.$t('Année'), value: 'year' },
        { text: this.$t('Couleur'), value: 'color' },
        { text: this.$t('N° Châssis'), value: 'vin' },
        { text: this.$t('Département'), value: 'department' },
        { text: this.$t('Type de carburant'), value: 'product' },
        { text: this.$t('Type de véhicule'), value: 'vehicle_type' },
        { text: this.$t('Nom conducteur'), value: 'driver_last_name' },
        { text: this.$t('Prénom conducteur'), value: 'driver_first_name' },
        {
          text: this.$t('Téléphone conducteur'),
          value: 'driver_phone_number'
        },
        { text: this.$t('Code secret'), value: 'secret_code' },
        {
          text: this.$t('Suivi kmh'),
          value: 'is_kmh',
          width: 100,
          align: 'center'
        },
        {
          text: this.$t('Bloqué'),
          value: 'is_blocked',
          width: 100,
          align: 'center'
        }
      ]
    },
    editableText () {
      return [
        'registration_number',
        'make',
        'model',
        'color',
        'vin',
        'vehicle_type',
        'driver_last_name',
        'driver_first_name',
        'driver_phone_number',
        'secret_code'
      ]
    },
    canImport () {
      return this.rows.length > 0 && this.validCount > 0
    },
    validCount () {
      return this.rows.filter(r => !r._errors || r._errors.length === 0).length
    },
    invalidCount () {
      return this.rows.length - this.validCount
    },
    company () {
      return this.$store.state.app.company || {}
    }
  },
  async created () {
    try {
      const prodResp = await ProductDetailService.getprincipalAll()
      this.products = prodResp.results || []
    } catch {
      this.products = []
    }

    try {
      if (this.company?.pk) {
        const deps = await DepartmentService.getDepartment(this.company.pk)
        this.departments = deps || []
      }
    } catch {
      this.departments = []
    }
  },
  methods: {
    // Helpers
    normalize (s) {
      return (s || '').toString().trim().toLowerCase()
    },
    parseIntOrEmpty (v) {
      const n = Number(
        String(v)
          .replace(/\s+/g, '')
          .replace(',', '.')
      )
      return Number.isFinite(n) ? n : ''
    },
    boolish (v) {
      const s = this.normalize(v)
      return ['1', 'true', 'oui', 'yes', 'y', 'vrai'].includes(s)
    },

    // Mapping véhicule FR -> codes Django
    resolveVehicleType (val) {
      if (val === null || val === undefined || val === '') return null
      const s = this.normalize(val)

      const map = {
        // Motorcycle
        motorcycle: 'Motorcycle',
        moto: 'Motorcycle',
        motocyclette: 'Motorcycle',

        // Truck
        truck: 'Truck',
        camion: 'Truck',
        'poids lourd': 'Truck',
        'poids-lourd': 'Truck',
        camionnette: 'Truck',
        'petit camion': 'Truck',

        // Tricycle
        tricycle: 'Tricycle',
        triporteur: 'Tricycle',

        // Sedan (Berline / VL personnel)
        sedan: 'Sedan',
        berline: 'Sedan',
        voiture: 'Sedan',
        personnel: 'Sedan',
        'vehicule personnel': 'Sedan',
        'véhicule personnel': 'Sedan',
        'voiture personnelle': 'Sedan',

        // SUV
        suv: 'SUV',
        '4x4': 'SUV',

        // PICK-UP
        'pick-up': 'PICK-UP',
        'pick up': 'PICK-UP',
        pickup: 'PICK-UP',

        // VL
        vl: 'VL',
        'véhicule léger': 'VL',
        'vehicule leger': 'VL',

        // OTHER
        autre: 'OTHER',
        other: 'OTHER'
      }

      return map[s] || null
    },
    isValidVehicleType (code) {
      return VEHICLE_TYPE_ALLOWED.includes(code)
    },

    resolveProductName (id) {
      if (id === '' || id === null || id === undefined) return null
      const p = this.products.find(x => x.id === Number(id))
      return p ? p.name : null
    },
    resolveDepartmentName (id) {
      if (id === '' || id === null || id === undefined) return null
      const d = this.departments.find(x => x.id === Number(id))
      return d ? d.name : null
    },
    // Produit : gestion Essence -> Super
    resolveProductToId (val) {
      if (val === null || val === undefined || val === '') return null
      if (!isNaN(Number(val))) return Number(val)
      let name = this.normalize(val)
      if (name === 'essence') {
        name = 'super'
      }
      const p = this.products.find(x => this.normalize(x.name) === name)
      return p ? p.id : null
    },
    resolveDepartmentToId (val) {
      if (val === null || val === undefined || val === '') return null
      if (!isNaN(Number(val))) return Number(val)
      const name = this.normalize(val)
      const d = this.departments.find(x => this.normalize(x.name) === name)
      return d ? d.id : null
    },
    resolveProductToIdOrKeep (val) {
      const id = this.resolveProductToId(val)
      return id ?? val ?? ''
    },
    resolveDepartmentToIdOrKeep (val) {
      const id = this.resolveDepartmentToId(val)
      return id ?? val ?? ''
    },

    // Dialog controls
    close () {
      if (this.busy) return
      this.reset()
      this.model = false
    },
    reset () {
      this.file = null
      this.rows = []
      this.mapping = {}
      this.errors = []
      this.busy = false
      this.total = 0
      this.done = 0
      this.progress = 0
      this.result = { errors: [] }
      this.defaultDepartmentId = null
      this.defaultProductId = null
    },

    // Template (FR)
    async downloadTemplate () {
      const headersFR = [
        'N° Matricule',
        'Marque',
        'Modèle',
        'Année',
        'Couleur',
        'N° Châssis',
        'Département',
        'Type de carburant',
        'Type de véhicule',
        'Nom du conducteur',
        'Prénom du conducteur',
        'Téléphone conducteur',
        'Code secret',
        'Suivi kmh (Oui/Non)',
        'Bloqué (Oui/Non)'
      ]
      const sample = [
        'AB-123-CD',
        'Toyota',
        'Corolla',
        '2019',
        'Gris',
        'JT12345XYZ',
        'Transport',
        'Gasoil',
        'Berline',
        'Kone',
        'Adama',
        '+2250700000001',
        '1234',
        'Oui',
        'Non'
      ]
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headersFR, sample])
      XLSX.utils.book_append_sheet(wb, ws, 'Template')
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      saveAs(
        new Blob([out], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        'modele_import_vehicules.xlsx'
      )
    },

    // Lecture & parsing
    buildMapping (headers) {
      const map = {}
      const norm = s =>
        (s || '')
          .toString()
          .trim()
          .toLowerCase()
      headers.forEach(h => {
        const hNorm = norm(h)
        for (const [field, aliases] of Object.entries(EXPECTED_HEADERS)) {
          if (aliases.map(a => norm(a)).includes(hNorm)) {
            map[field] = h // nom de colonne réel
          }
        }
      })
      if (!map.registration_number) {
        throw new Error(
          this.$t('Colonnes manquantes') + ' : N° Matricule'
        )
      }
      return map
    },

    async onFileChange () {
      this.errors = []
      this.rows = []
      if (!this.file) return
      try {
        const buf = await this.file.arrayBuffer()
        const wb = XLSX.read(buf, { type: 'array' })
        const sheetName = wb.SheetNames[0]
        const ws = wb.Sheets[sheetName]
        if (!ws) throw new Error(this.$t('Aucune feuille trouvée'))

        const json = XLSX.utils.sheet_to_json(ws, { defval: '' })
        if (!json.length) {
          this.errors = [this.$t('Aucune donnée détectée')]
          return
        }

        const headers = Object.keys(json[0] || {})
        this.mapping = this.buildMapping(headers)

        this.rows = json
          .map((row, i) => {
            const obj = { _row: i + 2 }

            // mappage des champs
            Object.entries(this.mapping).forEach(([field, key]) => {
              obj[field] = row[key] ?? ''
            })

            // normalisations numériques
            if (obj.year) obj.year = this.parseIntOrEmpty(obj.year)

            // bool
            obj.is_kmh = this.boolish(obj.is_kmh)
            obj.is_blocked = this.boolish(obj.is_blocked)

            // type de véhicule -> code Django si possible
            if (obj.vehicle_type) {
              const vt = this.resolveVehicleType(obj.vehicle_type)
              if (vt) obj.vehicle_type = vt
            }

            // Dept / Produit
            let depId = this.resolveDepartmentToId(obj.department)
            if (!depId && this.defaultDepartmentId) depId = this.defaultDepartmentId
            obj.department = depId ?? (obj.department || '')

            let prodId = this.resolveProductToId(obj.product)
            if (!prodId && this.defaultProductId) prodId = this.defaultProductId
            obj.product = prodId ?? (obj.product || '')

            this.validateRow(obj)

            // ignorer lignes totalement vides
            if (
              obj.registration_number ||
              obj.vin ||
              obj.driver_last_name ||
              obj.driver_first_name
            ) {
              return obj
            }
            return null
          })
          .filter(Boolean)
      } catch (e) {
        this.errors.push(e?.message || String(e))
      }
    },

    // Validation
    validateAll () {
      this.rows.forEach(r => this.validateRow(r))
    },
    validateRow (r) {
      const errs = []

      // Matricule obligatoire
      if (!String(r.registration_number || '').trim().length) {
        errs.push(this.$t('Matricule requis'))
      }

      // Type de véhicule obligatoire + doit être un code autorisé
      if (!String(r.vehicle_type || '').trim()) {
        errs.push(this.$t('Type de véhicule requis'))
      } else {
        // remapper ici aussi au cas où l'utilisateur édite en FR
        const vt = this.resolveVehicleType(r.vehicle_type) || r.vehicle_type
        r.vehicle_type = vt
        if (!this.isValidVehicleType(r.vehicle_type)) {
          errs.push(
            this.$t(
              'Type de véhicule invalide (valeurs autorisées : Moto, Camion, Camionnette, Tricycle, Berline/Personnel, SUV, Pick-up, Autre, VL)'
            )
          )
        }
      }

      // Produit requis (ID après résolution)
      if (!r.product || isNaN(Number(r.product))) {
        errs.push(this.$t('Type de carburant requis (ID ou nom)'))
      }

      // Département facultatif mais si fourni => ID
      if (
        r.department !== '' &&
        r.department !== null &&
        isNaN(Number(r.department))
      ) {
        errs.push(this.$t('Département inconnu (ID ou nom attendu)'))
      }

      // Nom / prénom / téléphone conducteur OPTIONNELS
      // if (!String(r.driver_last_name||'').trim()) errs.push(this.$t('Nom du conducteur requis'))
      // if (!String(r.driver_first_name||'').trim()) errs.push(this.$t('Prénom du conducteur requis'))
      // if (!String(r.driver_phone_number||'').trim()) errs.push(this.$t('Téléphone du conducteur requis'))

      // Code secret OPTIONNEL
      // if (!String(r.secret_code||'').trim()) errs.push(this.$t('Code secret requis'))

      if (
        r.year !== '' &&
        r.year !== null &&
        !Number.isInteger(Number(r.year))
      ) {
        errs.push(this.$t('Année invalide'))
      }
      r._errors = errs
      return errs.length === 0
    },

    // Import ligne à ligne
    /*async importRows () {
      if (!this.canImport) return
      this.busy = true
      this.result = { errors: [] }
      this.total = this.rows.length
      this.done = 0
      this.progress = 0

      const toImport = this.rows.filter(
        r => !r._errors || r._errors.length === 0
      )

      for (const r of toImport) {
        const payload = {
          registration_number: r.registration_number || null,
          make: r.make || null,
          model: r.model || null,
          year:
            r.year === '' || r.year === null ? null : Number(r.year),
          color: r.color || null,
          vin: r.vin || null,
          department: r.department ? Number(r.department) : null,
          product: r.product ? Number(r.product) : null,
          vehicle_type: r.vehicle_type || null,
          driver_last_name: r.driver_last_name || null,
          driver_first_name: r.driver_first_name || null,
          driver_phone_number: r.driver_phone_number || null,
          secret_code: r.secret_code || null,
          is_kmh: !!r.is_kmh,
          is_blocked: !!r.is_blocked
        }

        try {
          await CompanyVehiclesService.create(this, payload)
        } catch (e) {
          const msg = this.buildApiError(e)
          const label =
            [r.driver_last_name, r.driver_first_name]
              .filter(Boolean)
              .join(' ') ||
            r.registration_number ||
            r.vin ||
            'ligne'
          this.result.errors.push(`${label} — ${msg}`)
        } finally {
          this.done += 1
          this.progress = Math.round((this.done / this.total) * 100)
        }
      }

      this.$emit('imported', {
        total: this.total,
        failed: this.result.errors.length
      })
      if (this.result.errors.length === 0) {
        this.$store.commit('app/message', {
          msg: this.$t('Import terminé avec succès'),
          color: 'success'
        })
        this.close()
      } else {
        this.$store.commit('app/message', {
          msg: this.$t('Import terminé avec des erreurs'),
          color: 'warning'
        })
      }
      this.busy = false
    },*/
    async importRows () {
      if (!this.canImport) return
      this.busy = true
      this.result = { errors: [] }
      this.total = this.rows.length
      this.done = 0
      this.progress = 0

      const toImport = this.rows.filter(
        r => !r._errors || r._errors.length === 0
      )

      for (const r of toImport) {
        const payload = {
          registration_number: r.registration_number || null,
          make: r.make || null,
          model: r.model || null,
          year:
            r.year === '' || r.year === null ? null : Number(r.year),
          color: r.color || null,
          vin: r.vin || null,
          department: r.department ? Number(r.department) : null,
          product: r.product ? Number(r.product) : null,
          vehicle_type: r.vehicle_type || null,
          driver_last_name: r.driver_last_name || null,
          driver_first_name: r.driver_first_name || null,
          driver_phone_number: r.driver_phone_number || null,
          secret_code: r.secret_code || null,
          is_kmh: !!r.is_kmh,
          is_blocked: !!r.is_blocked
        }

        try {
          await CompanyVehiclesService.create(this, payload)
        } catch (e) {
          const msg = this.buildApiError(e)
          const label =
            [r.driver_last_name, r.driver_first_name]
              .filter(Boolean)
              .join(' ') ||
            r.registration_number ||
            r.vin ||
            'ligne'
          this.result.errors.push(`${label} — ${msg}`)
        } finally {
          this.done += 1
          this.progress = Math.round((this.done / this.total) * 100)
        }
      }

      const hasErrors = this.result.errors.length > 0

      if (!hasErrors) {
        // ✅ SUCCESS : on émet, le parent peut recharger la liste
        this.$emit('imported', {
          total: this.total,
          failed: 0
        })
        this.$store.commit('app/message', {
          msg: this.$t('Import terminé avec succès'),
          color: 'success'
        })
        this.close()
      } else {
        // ⚠️ ERREURS : on n’émet PAS "imported" -> donc pas de reload côté parent
        this.$store.commit('app/message', {
          msg: this.$t('Import terminé avec des erreurs'),
          color: 'warning'
        })
        // la modal reste ouverte, les erreurs sont visibles dans this.result.errors
      }

      this.busy = false
    },

    buildApiError (err) {
      if (err?.response?.data) {
        const data = err.response.data
        try {
          if (typeof data === 'string') return data
          const parts = []
          Object.keys(data).forEach(k => {
            const v = data[k]
            if (Array.isArray(v)) parts.push(`${k}: ${v.join(', ')}`)
            else if (typeof v === 'object' && v !== null) {
              Object.keys(v).forEach(sk =>
                parts.push(`${k}.${sk}: ${v[sk]}`)
              )
            } else parts.push(`${k}: ${v}`)
          })
          return parts.join(' | ')
        } catch {
          return this.$t('Erreur inconnue')
        }
      }
      return err?.message || this.$t('Erreur inconnue')
    },

    getRowClass (item) {
      return item._errors && item._errors.length ? 'row-error' : ''
    }
  }
}
</script>

<style scoped>
.row-error td {
  background-color: #ffebee !important;
}
</style>
